import {MRT_SortingState} from 'material-react-table';
import {useCallback, useMemo} from 'react';
import type {
  RecruitmentSortBy,
  RecruitmentSortingState,
  SortOrder,
} from '../types';

/**
 * Bidirectional mapping between table column IDs and API field names
 */
const COLUMN_TO_API_MAP: Record<string, RecruitmentSortBy> = {
  createdAt: 'created_at',
  status: 'status',
  name: 'name',
  email: 'email',
};

const API_TO_COLUMN_MAP: Record<RecruitmentSortBy, string> = {
  created_at: 'createdAt',
  status: 'status',
  name: 'name',
  email: 'email',
};

/**
 * Default sort order for each field
 */
const DEFAULT_SORT_ORDER: Record<RecruitmentSortBy, SortOrder> = {
  created_at: 'desc', // Newest first
  status: 'asc', // Alphabetical
  name: 'asc', // Alphabetical
  email: 'asc', // Alphabetical
};

/**
 * Custom hook that encapsulates all recruitment sorting logic
 *
 * @param sorting - Current sorting configuration (sortBy field and sortOrder direction)
 * @param setSorting - Callback to update the sorting state
 * @returns Handlers and computed values for sorting
 */
function useRecruitmentSorting(
  sorting: RecruitmentSortingState,
  setSorting: (sorting: RecruitmentSortingState | null) => void
) {
  // Map API sorting state to MRT table format
  const tableSorting = useMemo(
    (): MRT_SortingState => [
      {
        id: API_TO_COLUMN_MAP[sorting.sortBy],
        desc: sorting.sortOrder === 'desc',
      },
    ],
    [sorting]
  );

  // Apply sorting by field name with default sort order
  const handleToolbarSortChange = useCallback(
    (sortBy: RecruitmentSortBy) => {
      const sortOrder = DEFAULT_SORT_ORDER[sortBy];
      setSorting({sortBy, sortOrder});
    },
    [setSorting]
  );

  // Transform table sorting state to API format
  const handleTableSortChange = useCallback(
    (newSorting: MRT_SortingState) => {
      // Empty array: toggle current sort order (prevents reset loop)
      if (newSorting.length === 0) {
        const toggledOrder: SortOrder =
          sorting.sortOrder === 'desc' ? 'asc' : 'desc';
        setSorting({
          sortBy: sorting.sortBy,
          sortOrder: toggledOrder,
        });
        return;
      }

      // Map UI column ID to API field name
      const sort = newSorting[0];
      const sortBy = COLUMN_TO_API_MAP[sort.id];

      // Unknown column: keep current sorting
      if (!sortBy) {
        return;
      }

      // Apply new sorting
      setSorting({
        sortBy,
        sortOrder: (sort.desc ? 'desc' : 'asc') as SortOrder,
      });
    },
    [sorting, setSorting]
  );

  return {
    tableSorting,
    handleToolbarSortChange,
    handleTableSortChange,
  };
}

export default useRecruitmentSorting;
